// Storage helpers — hybrid localStorage + API

// Defaults to same-origin (`/api`) so this works out of the box when the
// frontend and backend are deployed together, e.g. on Vercel.
const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

// ---- AUTH / USER ----
export function getUser() {
  const raw = localStorage.getItem("hunch_user");
  return raw ? JSON.parse(raw) : null;
}

export function setUser(user) {
  localStorage.setItem("hunch_user", JSON.stringify(user));
}

export function clearUser() {
  localStorage.removeItem("hunch_user");
  clearToken();
}

export function getUserId() {
  const user = getUser();
  return user?.id || "";
}

export function getUsername() {
  const user = getUser();
  return user?.username || "";
}

export function getUniversity() {
  const user = getUser();
  return user?.university || "";
}

export function getPoints() {
  const user = getUser();
  return user?.points ?? 0;
}

export function updatePoints(newPoints) {
  const user = getUser();
  if (user) {
    user.points = newPoints;
    setUser(user);
  }
}

// ---- SESSION TOKEN ----
// Auth is now real: the server issues a signed token on verification, and
// every user-scoped request must send it. There is no more trusting a
// client-supplied user_id.
export function getToken() {
  return localStorage.getItem("hunch_token") || "";
}

export function setToken(token) {
  localStorage.setItem("hunch_token", token);
}

export function clearToken() {
  localStorage.removeItem("hunch_token");
}

function authHeaders(extra = {}) {
  const token = getToken();
  return token ? { ...extra, Authorization: `Bearer ${token}` } : extra;
}

// ---- API Calls ----
export async function apiRegister(email) {
  const res = await fetch(`${API}/auth/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Registration failed");
  return data;
}

export async function apiVerify(email, code, username) {
  const res = await fetch(`${API}/auth/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, code, username }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Verification failed");
  if (data.access_token) setToken(data.access_token);
  return data;
}

export async function apiLogin(email) {
  // No longer returns a user — only requests a verification code. The
  // caller must follow up with apiVerify(email, code) to actually log in.
  const res = await fetch(`${API}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Login failed");
  return data;
}

export async function apiGetMe() {
  if (!getToken()) return null;
  const res = await fetch(`${API}/auth/me`, { headers: authHeaders() });
  if (!res.ok) return null;
  const data = await res.json();
  return data.user;
}

export async function apiPlacePrediction(fixtureId, team) {
  const res = await fetch(`${API}/predictions`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ fixture_id: fixtureId, team }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Failed to place prediction");
  window.dispatchEvent(new Event("predictions-updated"));
  return data;
}

export async function apiCancelPrediction(fixtureId) {
  const res = await fetch(`${API}/predictions/${fixtureId}`, { method: "DELETE", headers: authHeaders() });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Failed to cancel");
  window.dispatchEvent(new Event("predictions-updated"));
  return data;
}

export async function apiGetPredictions() {
  const res = await fetch(`${API}/predictions`, { headers: authHeaders() });
  if (!res.ok) return [];
  const data = await res.json();
  return data.predictions || [];
}

export async function apiGetUniLeaderboard(uniName) {
  const res = await fetch(`${API}/leaderboard/university/${encodeURIComponent(uniName)}`);
  if (!res.ok) return { university: uniName, leaderboard: [] };
  return await res.json();
}

export async function apiGetCrossUniLeaderboard() {
  const res = await fetch(`${API}/leaderboard/cross-university`);
  if (!res.ok) return { standings: [] };
  return await res.json();
}

// ---- LEAGUES (API-backed) ----
export async function apiCreateLeague(name, description, maxMembers) {
  const res = await fetch(`${API}/leagues`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ name, description, max_members: maxMembers }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Failed to create league");
  return data.league;
}

export async function apiJoinLeague(code) {
  const res = await fetch(`${API}/leagues/join`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ code }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Failed to join league");
  return data.league;
}

export async function apiGetMyLeagues() {
  const res = await fetch(`${API}/leagues/my`, { headers: authHeaders() });
  if (!res.ok) return [];
  const data = await res.json();
  return data.leagues || [];
}

export async function apiGetLeague(leagueId) {
  const res = await fetch(`${API}/leagues/${leagueId}`);
  if (!res.ok) return null;
  const data = await res.json();
  return data.league;
}

export async function apiLeaveLeague(leagueId) {
  const res = await fetch(`${API}/leagues/${leagueId}/leave`, {
    method: "POST",
    headers: authHeaders(),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Failed to leave league");
  return data;
}

export async function apiDeleteLeague(leagueId) {
  const res = await fetch(`${API}/leagues/${leagueId}`, { method: "DELETE", headers: authHeaders() });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Failed to delete league");
  return data;
}

export async function apiRemoveMember(leagueId, memberId) {
  const res = await fetch(`${API}/leagues/${leagueId}/members/${memberId}`, { method: "DELETE", headers: authHeaders() });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Failed to remove member");
  return data;
}

// ---- FIXTURES ----
export async function apiGetFixtures() {
  const res = await fetch(`${API}/fixtures`);
  if (!res.ok) return [];
  return await res.json();
}

export async function apiCreateFixture(fixture) {
  const res = await fetch(`${API}/admin/fixtures`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(fixture),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Failed to create fixture");
  return data.fixture;
}

export async function apiUpdateFixture(fixtureId, fixture) {
  const res = await fetch(`${API}/admin/fixtures/${fixtureId}`, {
    method: "PUT",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(fixture),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Failed to update fixture");
  return data.fixture;
}

export async function apiDeleteFixture(fixtureId) {
  const res = await fetch(`${API}/admin/fixtures/${fixtureId}`, { method: "DELETE", headers: authHeaders() });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Failed to delete fixture");
  return data;
}

// ---- RESULTS ----
export async function apiGetResults() {
  const res = await fetch(`${API}/results`);
  if (!res.ok) return [];
  const data = await res.json();
  return data.results || [];
}

export async function apiSetResult(fixtureId, winner) {
  const res = await fetch(`${API}/admin/results`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ fixture_id: fixtureId, winner }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Failed to set result");
  return data;
}

// Legacy compat — kept for any remaining references
export function setUsername(name) {
  const user = getUser();
  if (user) { user.username = name; setUser(user); }
}
